import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Icon } from "../../../components/Component";
import { Button, Card, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import { Badge, DatePicker, Drawer, Table, Switch } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import SearchInput from "../../form-components/SearchComponent";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import { getDirtyFieldsOnly } from "../../../utils/use-form/GetDirtyFieldsOnly";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import dayjs from "dayjs";
import { RootState } from "../../../store/store";
import { expensesApis } from "../../../api/Expenses";
import reportApis from "../../../api/reports";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const { RangePicker } = DatePicker;

const ExpenseVendorReport: FC = () => {
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const { data: RootPageRenderState } = useSelector(
    (state: RootState) => state.RootPageRenderState
  );
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ projectId: string }>();
  const projectIdFromUrl = params.projectId;
  const { id: projectIdFromState, projectId: projectIdAlt, projectName } = location.state || {};
  const projectId = projectIdFromUrl || projectIdFromState || projectIdAlt;
  
  const methods = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = methods;
  const [initialCall, setInitialCall] = useState(true);
  const [dataSource, setDataSource] = useState<any>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setSearchQuery] = useState(null);
  const [filtersModal, setFiltersModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<any>();
  const [activeFilters, setActiveFiltered] = useState<any>({});
  const [showChart, setShowChart] = useState(false);

  const selectFilter1 = watch("selectFilter1");
  const selectFilter2 = watch("selectFilter2");
  const selectFilter3 = watch("selectFilter3");

  // Chart helpers (expenses by vendor/merchant)
  const getChartData = () => {
    if (!dataSource || dataSource.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            label: "Expense Amount (₹)",
            data: [],
            backgroundColor: "#3B82F6",
            borderColor: "#1D4ED8",
            borderWidth: 1,
          },
        ],
      };
    }

    const labels = dataSource.map((i: any) => i.merchant || "-");
    const data = dataSource.map((i: any) => i.expenseAmount || 0);
    return {
      labels,
      datasets: [
        {
          label: "Expense Amount (₹)",
          data,
          backgroundColor: "#3B82F6",
          borderColor: "#1D4ED8",
          borderWidth: 1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: "Expenses by Merchant",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value: any) {
            return "₹" + Number(value).toLocaleString();
          },
        },
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
      } = params;
      
      const {
        customDateRange,
      } = activeFilters;

      // Build API payload following DailyProgressDigest pattern
      const apiPayload: any = {
        reportName: "expenseByMerchant",
        projectIds: projectId ? Number(projectId) : undefined,
        page: current || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        direction: direction || "P",
        pageDiff: pageDiff || 0,
        ...(paginationId && { paginationId }),
        ...(searchQuery && { searchQuery }),
      };

      // Add filters with DateRange
      if (customDateRange && customDateRange[0] && customDateRange[1]) {
        apiPayload.startDate = dayjs(customDateRange[0]).format("YYYY-MM-DD");
        apiPayload.endDate = dayjs(customDateRange[1]).format("YYYY-MM-DD");
      }

      const response = await reportApis.fetchExpenseReports(apiPayload);

      if (response && response.status) {
        const reportsList = response.data?.list || [];
        const totalCount = response.data?.count || 0;

        setPagination((prev) => ({
          ...prev,
          total: Number(totalCount),
        }));
        setDataSource((prev: any) => (append ? [...prev, ...reportsList] : reportsList));
      } else {
        setDataSource([]);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };
    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1;
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current ? "F" : "B";
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.paginationId
            : dataSource[0]?.paginationId;
        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }
    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
    });
  };

  const refreshListView = (from?: string) => {
    try {
      if (from === "reset" || from === "clear all filters") {
        setPagination({
          current: 1,
          pageSize: 15,
          defaultCurrent: 1,
          defaultPageSize: 15,
          showSizeChanger: true,
          pageSizeOptions: ["15", "20", "30", "40", "50"],
          total: 0,
        });
      }
      fetchData({
        oldPage: 1,
        current: pagination.current.toString(),
        sortOrder: "DESC",
        pageSize: pagination.pageSize.toString(),
        direction: "P",
        pageDiff: 0,
        paginationId: null,
        searchQuery,
      });
    } catch (error) {}
  };

  useEffect(() => {
    refreshListView("filter");
  }, [activeFilters]);

  useEffect(() => {
    if (initialCall) {
      setInitialCall(false);
    } else refreshListView("RootPageRenderState change");
  }, [RootPageRenderState]);

  const refreshData = (overRideData?: any) => {
    fetchData({
      oldPage: 1,
      current: pagination.current.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: pagination.current - 1,
      paginationId: null,
      searchQuery,
      ...overRideData,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  // Define columns for expense vendor report
  const columns = [
    {
      title: "VENDOR",
      dataIndex: "merchant",
      key: "merchant",
      width: 250,
      ellipsis: true,
      render: (merchant: string, record: any) => (
        <span
          // style={{
          //   color: '#2557d6',
          //   cursor: 'pointer',
          //   textDecoration: 'underline',
          //   fontWeight: '500',
          //   fontSize: '14px',
          //   fontFamily: 'Inter, Roboto, sans-serif',
          //   whiteSpace: 'nowrap'
          // }}
          // onClick={() => navigate('/pms/expenses/expense-details-report', {
          //   state: {
          //     ...location.state,
          //     vendorId: record.id,
          //     vendorName: merchant,
          //     fromExpenseVendorReport: true,
          //   }
          // })}
          // title="Click to view vendor expense details"
        >
          {merchant}
        </span>
      ),
    },
    {
      title: "EXPENSE AMOUNT",
      dataIndex: "expenseAmount",
      key: "expenseAmount",
      width: 150,
      ellipsis: true,
      render: (amount: number) => (
        <span style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif' }}>₹{amount?.toLocaleString() || 0}.00</span>
      ),
    },
    {
      title: "EXPENSE COUNT",
      dataIndex: "expenseCount",
      key: "expenseCount",
      width: 120,
      ellipsis: true,
      render: (count: number) => (
        <span style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif' }}>{count || 0}</span>
      ),
    },
    {
      title: "EXPENSE AMOUNT WITHOUT TAX",
      dataIndex: "expenseWithoutTax",
      key: "expenseWithoutTax",
      width: 200,
      ellipsis: true,
      render: (amount: number) => (
        <span style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif' }}>₹{amount?.toLocaleString() || 0}.00</span>
      ),
    },
  ];

  return (
    <>
      <Head title="Expense Vendor Report" />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div
              className="card-inner card-inner-lg"
              style={{ minHeight: "87vh" }}
            >
              <div className="mb-3">
                <h5 style={{ marginBottom: "5px", fontWeight: "600" }}>
                  {projectName || "Expense Vendor Report"} - Expense Vendor Report
                </h5>
              </div>
              
              <FormProvider {...methods}>
                <form>
                  <div
                    className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
                    style={{ minHeight: "40px", border: "0px" }}
                  >
                    <div className="nk-ibx-head-actions w-45 ms-1">
                      <ul className="nk-ibx-head-tools g-1 m-0 p-0 w-100 ">
                        <div className={`w-100 m-0 `}>
                          <SearchInput
                            name="search"
                            placeholder="Search..."
                            width="100%"
                            prefixIcon={<SearchOutlined />}
                            onDebouncedSearch={async (value: any) => {
                              setSearchQuery(value);
                              setPagination({
                                ...pagination,
                                current: 1,
                              });
                              await fetchData({
                                oldPage: 1,
                                current: 1,
                                sortOrder: "DESC",
                                pageSize: pagination.pageSize.toString(),
                                direction: "P",
                                pageDiff: 0,
                                paginationId: null,
                                searchQuery: value,
                              });
                            }}
                            debounceDelay={300}
                          />
                        </div>
                      </ul>
                    </div>
                    <ul className="nk-ibx-head-tools g-1 m-0 p-0 ">
                      <li className="pt-0 pb-0">
                        <div className="d-flex align-items-center me-3">
                          <span className="me-2" style={{ fontSize: '14px', fontWeight: '500' }}>Show Chart:</span>
                          <Switch
                            checked={showChart}
                            onChange={setShowChart}
                            size="small"
                          />
                        </div>
                      </li>
                      {!showChart && (
                        <>
                          <li>
                            <UncontrolledDropdown>
                              <DropdownToggle
                                tag="a"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                                onClick={(ev) => {
                                  refreshListView("refresh");
                                }}
                              >
                                <Icon name="redo"></Icon>
                              </DropdownToggle>
                            </UncontrolledDropdown>
                          </li>
                          <li className="me-n1">
                            <a
                              href="#filter"
                              onClick={(ev) => {
                                ev.preventDefault();
                                setFiltersModal(true);
                              }}
                              className={`btn-icon me-2`}
                            >
                              <Badge
                                color="geekblue"
                                size="small"
                                count={Object.keys(activeFilters)?.length || 0}
                              >
                                <Icon
                                  style={{ fontSize: "20px" }}
                                  name="filter"
                                ></Icon>
                              </Badge>
                            </a>
                          </li>
                        </>
                      )}
                      <li className="pt-0 pb-0">
                        <Button
                          className="toggle"
                          color="primary"
                          size="sm"
                          onClick={() => {
                            const path = projectId != null && !isNaN(Number(projectId))
                              ? `/pms/expenses/ExpensesReports/${projectId}`
                              : '/pms/expenses/ExpensesReports';
                            navigate(path, {
                              state: { ...location.state, id: projectId, projectId: projectId },
                            });
                          }}
                        >
                          <Icon name={"arrow-left"} />
                          <span>Back</span>
                        </Button>
                      </li>
                    </ul>
                  </div>
                </form>
              </FormProvider>

              <div className="card-inner card-inner-lg p-0">
                <div className="content clearfix">
                  <div
                    style={{
                      width: screenSize - (SideNavCollapse ? 120 : 320) + "px",
                      margin: "10px auto",
                    }}
                  >
                    {showChart ? (
                      <div style={{ padding: "20px", backgroundColor: "#fff", borderRadius: "8px", width: "100%" }}>
                        <div style={{ marginBottom: "20px" }}>
                          <h6 style={{ margin: 0, fontWeight: "600" }}>Expenses by Merchant</h6>
                          <p style={{ margin: "5px 0 0 0", color: "#666", fontSize: "14px" }}>
                            From {customDateRange ? dayjs(customDateRange[0]).format('DD-MMM-YYYY') : dayjs().startOf('month').format('DD-MMM-YYYY')} To {customDateRange ? dayjs(customDateRange[1]).format('DD-MMM-YYYY') : dayjs().endOf('month').format('DD-MMM-YYYY')}
                          </p>
                        </div>
                        <div style={{ height: "400px", width: "100%" }}>
                          <Bar data={getChartData()} options={chartOptions} />
                        </div>
                      </div>
                    ) : (
                      <SkeletonTable
                        loading={loading}
                        columns={columns as SkeletonTableColumnsType[]}
                      >
                        <Table
                          rowKey="id"
                          columns={columns}
                          dataSource={dataSource}
                          pagination={pagination}
                          onChange={tableOnChange}
                          bordered
                          size="middle"
                          scroll={{
                            x: "max-content",
                          }}
                        />
                      </SkeletonTable>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Content>
      <Drawer
        maskClosable={false}
        title={
          <div className="d-flex justify-content-between align-items-center">
            <strong>Filters</strong>
            <>
              <div style={{ textAlign: "right" }}>
                <Button
                  onClick={() => {
                    methods.reset();
                    setActiveFiltered({});
                    setCustomDateRange(null);
                  }}
                  color=""
                  className="m-3 mt-0 mb-0 ps-4 pe-4"
                  size="sm"
                >
                  <span style={{ color: "#0772f8" }}>Reset</span>
                </Button>
              </div>
            </>
          </div>
        }
        placement="right"
        width={450}
        onClose={() => {
          setFiltersModal(false);
        }}
        open={filtersModal}
        styles={{ body: { paddingBottom: 80 } }}
      >
        <div className="d-flex align-items-center flex-wrap w-100">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit((data) => {
                setFiltersModal(false);
                const dirtyData = getDirtyFieldsOnly(data, dirtyFields) || {};
                const isAllFiltersEmpty =
                  Object.keys(dirtyData).length === 0 &&
                  (!customDateRange ||
                    (customDateRange &&
                      !customDateRange[0] &&
                      !customDateRange[1]));
                if (isAllFiltersEmpty) {
                  setActiveFiltered({});
                  refreshListView("clear all filters");
                } else {
                  if (
                    customDateRange &&
                    customDateRange[0] &&
                    customDateRange[1]
                  ) {
                    dirtyData.customDateRange = customDateRange;
                  }
                  setActiveFiltered(dirtyData);
                }
              })}
              className="w-100"
            >
              <div key={"filters5"} className="mt-0 mb-2">
                <label className="form-label">Select Date Range</label>
                <RangePicker
                  name={`dateRange`}
                  value={
                    customDateRange
                      ? [
                          dayjs(customDateRange[0].toDate()),
                          dayjs(customDateRange[1].toDate()),
                        ]
                      : null
                  }
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setCustomDateRange([dates[0], dates[1]]);
                    } else {
                      setCustomDateRange(null);
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="actions clearfix">
                <ul>
                  <li>
                    <Button color="primary" size="sm" type="submit">
                      Find
                    </Button>
                  </li>
                  <li>
                    <Button
                      size="sm"
                      color="primary"
                      className="btn-dim btn btn-secondary"
                      type="button"
                      onClick={() => {
                        setFiltersModal(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </li>
                </ul>
              </div>
            </form>
          </FormProvider>
        </div>
      </Drawer>
    </>
  );
};

export default ExpenseVendorReport;

